import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import trim from 'lodash/trim';
import cloneDeep from 'lodash/cloneDeep';
import size from 'lodash/size';
import {
    themeSystemDefault,
    configurationSystemDefault
} from '../../../constants/SystemDefaults';
import {
    defaultValuesForProblematicKeys,
} from '../../../constants/DefaultValuesForProblematicKeys';
import { filterThemeUniqueGlobal } from '../../../constants/uniqueKeyToGlobalMap';
import { DOMAIN_ERROR_CODES } from '../../../constants/manageDomains';
import defaultsDeep from 'lodash/defaultsDeep';
import _ from 'lodash';
import isValidDomain from 'is-valid-domain';
import { updateAddOnsConfig } from '../../../utils/configuration-utils';

/**
 * Async implementation of setRouteLeaveHook waits for the result of the async operation and use it as a result
 *
 * router.setRouteLeaveHook fires every time when we call 'router.push' the method,
 * on the second execution we don't call 'hook' function again, just use result of the previous invocation
 * (if it is true, it allows redirect)
 * @param router - object
 * @param route - object
 * @param hook - function with Promise as return type
 * @returns {boolean}
 */
export const setAsyncRouteLeaveHook = (router, route, hook) => {
    let withinHook = false;
    let finalResult = undefined;
    let finalResultSet = false;
    return router.setRouteLeaveHook(route, nextLocation => {
        withinHook = true;
        if (!finalResultSet) {
            hook(nextLocation).then(result => {
                finalResult = result.finalResult;
                finalResultSet = true;
                if (!withinHook && nextLocation && !result.stayOnSamePage) {
                    router.push(nextLocation);
                }
            });
        }
        let result = finalResultSet ? finalResult : false;
        withinHook = false;
        finalResult = undefined;
        finalResultSet = false;
        return result;
    });
};

/**
 * gets a flattened list of object properties
 * @param sourceObject
 * @returns {an array of string paths}
 */
export const getFlattenedListOfObjectProperties = sourceObject => {
    const listOfProperties = [];
    setListOfProperties(sourceObject, '', listOfProperties);

    return listOfProperties;
};

const setListOfProperties = (obj, stack, listOfProperties) => {
    Object.keys(obj).forEach(property => {
        if (obj.hasOwnProperty(property)) {
            const fullProperty = `${stack}.${property}`;
            const fullPropertyWithLeadingDotRemoved = fullProperty.substring(1);
            if (Array.isArray(obj[property])) {
                listOfProperties.push(fullPropertyWithLeadingDotRemoved);
            } else if (typeof obj[property] === 'object') {
                if (isEmpty(obj[property])) {
                    listOfProperties.push(fullPropertyWithLeadingDotRemoved);
                } else {
                    setListOfProperties(obj[property], fullProperty, listOfProperties);
                }
            } else {
                listOfProperties.push(fullPropertyWithLeadingDotRemoved);
            }
        }
    });
};

/**
 * cleans theme json for download and editing in json editor.  removes obsolete keys, inserts system defaults where they are missing, handles problematic theme key values
 * TODO: remove this function when US856747 is worked
 * @param jsonFromResponse
 * @returns cleanedJson
 */
export function getCleanedThemeJson(jsonFromResponse) {
    const sysDefaultTheme = filterThemeUniqueGlobal(themeSystemDefault);
    const jsonFromResponseWithDefaultThemeValuesAdded = defaultsDeep(jsonFromResponse, sysDefaultTheme);
    const sysDefaultThemeKeys = getFlattenedListOfObjectProperties(sysDefaultTheme);
    // remove any obsolete keys that still populate valid keys like link.color.normal, so that they are not required (or displayed) during download and edit json file
    const adjustedSysDefaultThemeKeys = sysDefaultThemeKeys.filter(key => key !== 'link.color.normal');
    const jsonFromResponseWithObsoleteKeysRemoved = _.pick(
        jsonFromResponseWithDefaultThemeValuesAdded,
        adjustedSysDefaultThemeKeys
    );
    const cleanedJson = getCleanJsonWithProblematicKeyValuesAdded(
        jsonFromResponse,
        jsonFromResponseWithObsoleteKeysRemoved
    );

    return cleanedJson;
}

/**
 * cleans config json for download and editing in json editor.  removes obsolete keys, inserts system defaults where they are missing
 * @param jsonFromResponse
 * @returns cleanedJson
 */
export function getCleanedConfigJson(jsonFromResponse) {
    const jsonFromResponseWithDefaultValues = defaultsDeep(jsonFromResponse, configurationSystemDefault);
    const sysDefaultConfigKeys = getFlattenedListOfObjectProperties(configurationSystemDefault);
    updateAddOnsConfig(jsonFromResponseWithDefaultValues);
    const cleanedJson = _.pick(jsonFromResponseWithDefaultValues, sysDefaultConfigKeys);

    return cleanedJson;
}

/**
 * manually sets problematic key values into nested theme json
 * @param jsonFromResponse
 * @param cleanedThemeJson
 */
export function getCleanJsonWithProblematicKeyValuesAdded(jsonFromResponse, jsonFromResponseWithObsoleteKeysRemoved) {
    // get the values from the response. if none use defaults
    const gridRowBackground = get(jsonFromResponse, 'grid.row.background', '');
    const gridRowBackgroundAdjusted =
        typeof gridRowBackground === 'object' ? get(jsonFromResponse, 'background.style1', '') : gridRowBackground;
    const gridRowBackgroundHover = get(jsonFromResponse, 'grid.row.background.hover', '');
    const gridRowBackgroundHoverAdjusted = !gridRowBackgroundHover
        ? get(jsonFromResponse, 'background.style2', '')
        : gridRowBackgroundHover;
    const linkHighlightTextDecoration = get(
        jsonFromResponse,
        'link.highlight.textDecoration',
        defaultValuesForProblematicKeys.linkHighlightTextDecoration
    );
    const linkHighlightTextDecorationAdjusted =
        typeof linkHighlightTextDecoration === 'object'
            ? defaultValuesForProblematicKeys.linkHighlightTextDecoration
            : linkHighlightTextDecoration;
    const linkHighlightTextDecorationHover = get(
        jsonFromResponse,
        'link.highlight.textDecoration.hover',
        defaultValuesForProblematicKeys.linkHighlightTextDecorationHover
    );
    const linkHighlightTextDecorationActive = get(
        jsonFromResponse,
        'link.highlight.textDecoration.active',
        defaultValuesForProblematicKeys.linkHighlightTextDecorationActive
    );
    const boxShadow = get(jsonFromResponse, 'box.shadow', defaultValuesForProblematicKeys.boxShadow);
    const boxShadowAdjusted = typeof boxShadow === 'object' ? defaultValuesForProblematicKeys.boxShadow : boxShadow;
    const boxShadowStickyElements = get(
        jsonFromResponse,
        'box.shadow.stickyElements',
        defaultValuesForProblematicKeys.boxShadowStickyElements
    );
    const inputColor = get(jsonFromResponse, 'input.color', defaultValuesForProblematicKeys.inputColor);
    const inputColorAdjusted = typeof inputColor === 'object' ? defaultValuesForProblematicKeys.inputColor : inputColor;
    const inputColorDisabled = get(
        jsonFromResponse,
        'input.color.disabled',
        defaultValuesForProblematicKeys.inputColorDisabled
    );

    const cleanedThemeJson = cloneDeep(jsonFromResponseWithObsoleteKeysRemoved);

    // manually add in four missing abnormal keys
    const gridRowNode = get(cleanedThemeJson, 'grid.row', '');
    gridRowNode['background'] = gridRowBackgroundAdjusted;
    gridRowNode['background.hover'] = gridRowBackgroundHoverAdjusted;

    const linkHighlightNode = get(cleanedThemeJson, 'link.highlight');
    linkHighlightNode['textDecoration'] = linkHighlightTextDecorationAdjusted;
    linkHighlightNode['textDecoration.hover'] = linkHighlightTextDecorationHover;
    linkHighlightNode['textDecoration.active'] = linkHighlightTextDecorationActive;

    const boxNode = get(cleanedThemeJson, 'box');
    boxNode['shadow'] = boxShadowAdjusted;
    boxNode['shadow.stickyElements'] = boxShadowStickyElements;

    const inputNode = get(cleanedThemeJson, 'input');
    inputNode['color'] = inputColorAdjusted;
    inputNode['color.disabled'] = inputColorDisabled;

    return cleanedThemeJson;
}

/**
 * upgrade Schema from Draft04 to Draft07. adds required, minProperties, additionalProperties, and ids
 * @param schemaFromLibrary
 * @param schemaId
 * @param sourceJson
 * @param keysThatShouldBePresentButNotRequired
 * @returns upgraded schema
 */
export function getUpgradedJsonSchema(
    schemaFromLibrary,
    schemaId,
    sourceJson,
    keysThatShouldBePresentButNotRequired = []
) {
    const flattenedListOfProperties = getFlattenedListOfObjectProperties(sourceJson);
    const listOfStringPropertiesWhichCanBeEmpty = flattenedListOfProperties.filter(key => get(sourceJson, key) === '');
    const schema = cloneDeep(schemaFromLibrary);
    schema['$schema'] = 'http://json-schema.org/draft-07/schema#';
    schema['$id'] = schemaId;
    schema['minProperties'] = 1;
    schema['additionalProperties'] = false;
    schema['required'] = Object.keys(schema.properties).filter(key => {
        return !keysThatShouldBePresentButNotRequired.includes(key);
    });

    const addRequiredPropertiesList = (obj, stack) => {
        Object.keys(obj).forEach(property => {
            const fullStack = `${stack}.${property}`;
            const fullStackWithoutLeadingDot = fullStack.slice(1);
            if (obj[property].type === 'object') {
                if (!_.isEmpty(obj[property].properties)) {
                    obj[property]['minProperties'] = 1;
                    obj[property]['additionalProperties'] = false;
                }
                if (!obj[property].hasOwnProperty('required')) {
                    obj[property]['required'] = Object.keys(obj[property].properties).filter(key => {
                        const test = `${fullStackWithoutLeadingDot}.${key}`;
                        return !keysThatShouldBePresentButNotRequired.includes(test);
                    });
                }
                addRequiredPropertiesList(obj[property].properties, fullStack);
            } else if (
                obj[property].type === 'string' &&
                !listOfStringPropertiesWhichCanBeEmpty.includes(fullStackWithoutLeadingDot)
            ) {
                obj[property]['minLength'] = 1;
            }
        });
    };

    const setIds = (obj, stack) => {
        Object.keys(obj).forEach(property => {
            const fullProperty = `${stack}/properties/${property}`;
            if (typeof obj[property] === 'object') {
                if (property === 'properties') {
                    setIds(obj[property], `${stack}/items`);
                } else {
                    obj[property]['$id'] = `#${fullProperty}`;
                }
                if (obj[property].hasOwnProperty('properties')) {
                    setIds(obj[property]['properties'], fullProperty);
                }
                if (obj[property].hasOwnProperty('items')) {
                    setIds(obj[property]['items'], fullProperty);
                    obj[property]['items']['$id'] = `#${fullProperty}/items`;
                }
            }
        });
    };

    addRequiredPropertiesList(schema.properties, '');
    setIds(schema.properties, '');

    return schema;
}

/**
 * should return boolean value if domain is valid
 * @param {String} domain
 * @returns {boolean}
 */
export const isDomainValid = domain => {
    const validationSettings = { subdomain: true, wildcard: true };
    const replacePrefixes = ['https://', 'http://', 'ws://', 'wss://'];

    for (const prefix of replacePrefixes) {
        if (domain.includes(prefix)) {
            domain = domain.replace(prefix, '');
        }
    }

    const domainParts = domain.split(':');

    if (size(domainParts) > 2) {
        return false;
    }

    const domainWithoutPort = domain.includes(':') ? _.head(domainParts) : domain;

    return isValidDomain(domainWithoutPort, validationSettings);
};

/**
 * should return error code
 * @param {Array} domains
 * @param {String} inputData
 * @returns {String}
 */
export const domainValidator = (domains, inputData) => {
    const inputDomain = trim(inputData);
    const isWildcardDomain = inputDomain.includes('*');

    if (size(inputDomain) < 1) {
        return DOMAIN_ERROR_CODES.EMPTY;
    }

    const isDuplicatedDomain = !isEmpty(domains.filter(domain => domain === inputDomain));

    if (isDuplicatedDomain) {
        return DOMAIN_ERROR_CODES.DUPLICATED;
    }

    if (isWildcardDomain && !isDomainValid(inputDomain)) {
        return DOMAIN_ERROR_CODES.INVALID_WILDCARD_DOMAIN;
    }

    if (!isDomainValid(inputDomain)) {
        return DOMAIN_ERROR_CODES.INVALID_DOMAIN;
    }

    return '';
};



// WEBPACK FOOTER //
// ./src/components/pages/manage/utils.js